import { provideAppInitializer } from '@angular/core';
import { inject as historyInject } from '@angular/core';
import { SearchHistoryService } from './search-history/search-history.service';
import { ListingFormControlsModule } from './shared/listing-form-controls.module';
import { UserSafetyActionsComponent } from './shared/components/user-safety-actions.component';
import { ModerationInboxComponent } from './shared/components/moderation-inbox.component';
import { blockContentMetaReducer } from './store/block-content';
import { SyrianCityPipe } from './shared/pipes/syrian-city.pipe';
import { SearchCountSummaryDirective } from './shared/components/dropdown-checkbox-select/search-count-summary.directive';
import { SearchFacetCountsDirective } from './shared/components/dropdown-checkbox-select/search-facet-counts.directive';
import { SaveSearchComponent } from './saved-searches/save-search.component';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgSelectModule } from '@ng-select/ng-select';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CanEditDirective } from './directives/can-edit.directive';
import { EmptyToUndefinedDirective } from './directives/empty-to-undefined.directive';
import { HighlightIfHasValueDirective } from './directives/highlight-if-has-value.directive';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { AboutUsComponent } from './pages/about-us/about-us.component';
import { CarDetailsComponent } from './pages/car-details/car-details.component';
import { CarImagesCarouselComponent } from './pages/car-images-carousel/car-images-carousel.component';
import { CarItemComponent } from './pages/car-item/car-item.component';
import { CarListComponent } from './pages/car-list/car-list.component';
import { CarMapItemComponent } from './pages/car-map-item/car-map-item.component';
import { CarSearchResultComponent } from './pages/car-search-result/car-search-result.component';
import { CarSearchComponent } from './pages/car-search/car-search.component';
import { DataProtectionComponent } from './pages/data-protection/data-protection.component';
import { EmailVerificationComponent } from './pages/email-verification/email-verification.component';
import { FaqComponent } from './pages/faq/faq.component';
import { FooterComponent } from './pages/footer/footer.component';
import { ForgetPasswordComponent } from './pages/forget-password/forget-password.component';
import { HomeComponent } from './pages/home/home.component';
import { LoginComponent } from './pages/login/login.component';
import { PrivacySettingsComponent } from './pages/privacy-settings/privacy-settings.component';
import { PropertyDetailsComponent } from './pages/property-details/property-details.component';
import { PropertyImagesCarouselComponentComponent } from './pages/property-images-carousel-component/property-images-carousel-component.component';
import { PropertyItemComponent } from './pages/property-item/property-item.component';
import { PropertyListComponent } from './pages/property-list/property-list.component';
import { PropertyMapItemComponent } from './pages/property-map-item/property-map-item.component';
import { PropertySearchResultComponent } from './pages/property-search-result/property-search-result.component';
import { PropertySearchComponent } from './pages/property-search/property-search.component';
import { RegisterComponent } from './pages/register/register.component';
import { ReportAdComponent } from './pages/report-ad/report-ad.component';
import { ReportsComponent } from './pages/reports/reports.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { SellerDetailsComponent } from './pages/seller-details/seller-details.component';
import { ShareButtonComponent } from './pages/share-button/share-button.component';
import { TermsConditionsComponent } from './pages/terms-conditions/terms-conditions.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { UpdatePasswordComponent } from './pages/update-password/update-password.component';
import { UserDetailsComponent } from './pages/user-details/user-details.component';
import { UserListComponent } from './pages/user-list/user-list.component';
import { UserMapItemComponent } from './pages/user-map-item/user-map-item.component';
import { UserSearchResultComponent } from './pages/user-search-result/user-search-result.component';
import { UsersComponent } from './pages/users/users.component';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { GoogleMapsLocationComponent } from './shared/components/google-maps-location/google-maps-location.component';
import { ItemLocationComponent } from './shared/components/item-location/item-location.component';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { MapResultComponent } from './shared/components/map-result/map-result.component';
import { PlanTypeComponent } from './shared/components/plan-type/plan-type.component';
import { PriceComponent } from './shared/components/price/price.component';
import { ScrollToTop } from './shared/components/scroll-to-top/scroll-to-top';
import { LanguageSwitcherComponent } from './shared/language-switcher/language-switcher.component';
import { AuthEffects } from './store/effects/auth.effects';
import { CarSearchEffects as CarEffects } from './store/effects/car.effects';
import { CategoriesEffects } from './store/effects/categories.effects';
import { RealEstateSearchEffects as RealEstateEffects } from './store/effects/real-estate.effects';
import { SyriaBoundaryEffects } from './store/effects/syria-boundary.effects';
import { UsersEffects } from './store/effects/users.effects';
import { authReducer } from './store/reducers/auth.reducer';
import { carReducer } from './store/reducers/car.reducer';
import { categoriesReducer } from './store/reducers/categories.reducer';
import { languageReducer } from './store/reducers/language.reducer';
import { realEstateReducer } from './store/reducers/real-estate.reducer';
import { syriaBoundaryReducer } from './store/reducers/syria-boundary.reducer';
import { usersReducer } from './store/reducers/users.reducer';
import { DeleteAccount } from './pages/delete-account/delete-account';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RegisterComponent,
    CarSearchComponent,
    CarDetailsComponent,
    SafeUrlPipe,
    UsersComponent,
    CanEditDirective,
    UnauthorizedComponent,
    UserDetailsComponent,
    CarListComponent,
    CarImagesCarouselComponent,
    EmailVerificationComponent,
    ForgetPasswordComponent,
    ResetPasswordComponent,
    ShareButtonComponent,
    LanguageSwitcherComponent,
    FaqComponent,
    SellerDetailsComponent,
    PlanTypeComponent,
    AboutUsComponent,
    PropertySearchComponent,
    PropertyListComponent,
    PropertyImagesCarouselComponentComponent,
    HomeComponent,
    PropertyDetailsComponent,
    UpdatePasswordComponent,
    UserListComponent,
    CarSearchResultComponent,
    PropertySearchResultComponent,
    UserSearchResultComponent,
    FooterComponent,
    ReportAdComponent,
    ReportsComponent,
    DataProtectionComponent,
    TermsConditionsComponent,
    PrivacySettingsComponent,
    HighlightIfHasValueDirective,
    EmptyToUndefinedDirective,
    MapResultComponent,
    ItemLocationComponent,
    CarItemComponent,
    CarMapItemComponent,
    PropertyItemComponent,
    PropertyMapItemComponent,
    UserMapItemComponent,
    GoogleMapsLocationComponent,
    ScrollToTop,
    PriceComponent,
    DeleteAccount,
  ],
  imports: [
    ListingFormControlsModule,
    LoadingComponent,
    UserSafetyActionsComponent, ModerationInboxComponent,
    SyrianCityPipe,
    SaveSearchComponent,
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right', // Change this to 'toast-bottom-right' or whatever you prefer
      timeOut: 3000,
      closeButton: true,
      progressBar: true,
    }),
    TranslateModule.forRoot({
      fallbackLang: 'ar',
      // Static translations load during DI bootstrap; they must not instantiate AuthInterceptor.
      loader: provideTranslateHttpLoader({ prefix: './i18n/', suffix: '.json', useHttpBackend: true }),
    }),
    StoreModule.forRoot({ categories: categoriesReducer }, { metaReducers: [blockContentMetaReducer] }),
    StoreModule.forFeature('car', carReducer),
    StoreModule.forFeature('realEstate', realEstateReducer),
    StoreModule.forFeature('users', usersReducer),
    StoreModule.forFeature('language', languageReducer),
    StoreModule.forFeature('auth', authReducer),
    StoreModule.forFeature('syriaBoundary', syriaBoundaryReducer),
    EffectsModule.forRoot([
      CategoriesEffects,
      CarEffects,
      RealEstateEffects,
      UsersEffects,
      AuthEffects,
      SyriaBoundaryEffects,
    ]),
    FormsModule,
    BrowserAnimationsModule,
    NgSelectModule,
    SearchFacetCountsDirective,
    SearchCountSummaryDirective,
  ],
  providers: [
    provideAppInitializer(() => { historyInject(SearchHistoryService); }),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
  exports: [ListingFormControlsModule, HighlightIfHasValueDirective, EmptyToUndefinedDirective],
})
export class AppModule {}
